/**
 * client/src/lib/continuous.ts — what happens when the book runs out at 2 a.m.
 *
 * `AudioEngine.endOfBook()` has always paused and emitted `end-of-book`, which is the correct
 * behaviour for a person reading in the afternoon and the wrong one for the way this library is
 * actually used: put a book on at night and the room goes silent at the end of the last page. This
 * module is the one place that answers "and then what", so the reader, the engine and the Media
 * Session cannot each have their own opinion.
 *
 * ── Three modes, and why "stop" is still one of them ─────────────────────────
 * A setting that cannot be turned off is a bug with a preference pane. `stop` is the behaviour every
 * build before this one had, and someone finishing a book they were *reading* wants the silence.
 *
 * ── Why the default is `next` ───────────────────────────────────────────────
 * Karti asked for this in the words *"so at night it doesn't stop playing"*. `repeat` satisfies the
 * letter of that and surprises the listener who fell asleep in chapter two and wakes to chapter two
 * again; `next` is the shape of every sleep timer, podcast queue and radio station, and it is the
 * mode that makes the *library* the unit rather than the book. Both are one tap apart in the
 * playback bar.
 *
 * ── The rule that makes `next` safe, and it is not obvious ──────────────────
 * ⚠️ **A book with no audio is not a candidate**, however good a next read it would be. Narration on
 * this site is generated lazily and by an overnight sweep, so most of the shelf is at 0 % at any
 * moment (measured 2026-08-13: 23 232 clips against 355 957 speakable sentences). Advancing into one
 * of those hands the engine a page that does not exist, `waitForNext` polls for three minutes and
 * then says *"Couldn't reach the voice service"* — so the failure mode of a feature whose entire
 * purpose is to keep playing would be to stop playing, with an error blaming the network. A book is
 * only a candidate if the shelf says it already has audible pages with audio.
 */

import type { ShelfCard } from "./api.ts";

export type ContinuousMode = "stop" | "repeat" | "next";

export const CONTINUOUS_MODES: readonly { key: ContinuousMode; label: string; hint: string }[] = [
  { key: "stop", label: "Stop", hint: "Silence at the end of the book." },
  { key: "repeat", label: "Repeat", hint: "Start this book again from chapter one." },
  { key: "next", label: "Next book", hint: "Move to the next narrated book on the shelf." },
];

export const DEFAULT_CONTINUOUS_MODE: ContinuousMode = "next";

const KEY = "ab.reader.continuous";

const isMode = (v: unknown): v is ContinuousMode => v === "stop" || v === "repeat" || v === "next";

export function loadContinuousMode(): ContinuousMode {
  try {
    const raw = localStorage.getItem(KEY);
    return isMode(raw) ? raw : DEFAULT_CONTINUOUS_MODE;
  } catch {
    // Safari private mode throws on access. The default is the honest answer, not a crash.
    return DEFAULT_CONTINUOUS_MODE;
  }
}

export function saveContinuousMode(mode: ContinuousMode): void {
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    /* Private browsing. The choice is lost on reload; playback still works. */
  }
}

/**
 * Has this book enough audio to be worth moving to?
 *
 * `pagesAudible` is the denominator this repo insists on everywhere else (`NarrationCoverage` carries
 * the whole argument): `pagesTotal` counts title pages and colophons that no GPU will ever narrate,
 * so a book can be fully narrated and never reach `pagesWithAudio === pagesTotal`. Asking for *any*
 * audible page with audio — rather than a percentage — is deliberate: a book that is 8 % narrated
 * still plays for forty minutes before it needs the next one, and by then the sweep has moved.
 */
export function hasPlayableAudio(card: ShelfCard): boolean {
  const n = card.narration;
  if (n === null || n === undefined) return false;
  return n.pagesAudible > 0 && n.pagesWithAudio > 0;
}

/**
 * The book after this one, in the order the shelf is already showing, wrapping past the end.
 *
 * Wrapping rather than stopping at Z is the whole point of the mode — a night that begins at *Walden*
 * must not end at *Zero to One* because the alphabet ran out. The current book is excluded even on
 * the wrap, so `next` never quietly becomes `repeat`; if it is the only narrated book on the shelf
 * the caller gets `null` and can fall back deliberately.
 */
export function pickNextBook(cards: readonly ShelfCard[], currentBookId: string): ShelfCard | null {
  const playable = cards.filter((c) => c.deletedAt == null && c.takenDownAt == null && hasPlayableAudio(c));
  if (playable.length === 0) return null;

  /**
   * ⚠️ `-1` is a real starting position, not a "not found" to be clamped to 0.
   *
   * The current book is often absent from the playable list — it is routinely the one book at 0 %
   * that a human pressed play on. The walk below starts at `here + 1`, so clamping a miss to 0 starts
   * it at 1 and **skips the first book on the shelf**, which is the one case where the whole shelf is
   * a candidate. Leaving it at -1 makes the first step land on index 0, which is the answer.
   */
  const here = playable.findIndex((c) => c.id === currentBookId);
  for (let step = 1; step <= playable.length; step++) {
    const card = playable[(here + step + playable.length) % playable.length]!;
    if (card.id !== currentBookId) return card;
  }
  return null;
}
